/* ----------------------------------------------------------------------------
 * stateVisualStyle — the PURE, DOM-free single source of truth for Aura's three
 * voice-state visuals (Listening / Thinking / Speaking) + the calm idle/ready
 * baseline. Every state->visual decision (orb glow colour, hero audio-wave,
 * control adornment + label) is expressed here so it can be unit-tested under
 * Node's native --test (no DOM, no react, no '@/' alias) exactly like
 * auraOrbStyle.ts. The react components (AuraMark, HeroStateWave,
 * ControlStateAdornment, VoiceCopilot) are thin consumers of this mapping.
 *
 * Design (owner references 2026-07-18):
 *   - listening : bright cyan/teal — orb teal glow, hero teal audio-wave,
 *                 control teal waveform. label "Listening".
 *   - thinking  : violet — orb slower thoughtful violet pulse, NO hero wave,
 *                 control violet processing dots. label "Thinking".
 *   - speaking  : amber centre + coral/magenta outer — orb amber/coral glow,
 *                 hero WARM audio-wave, control amber waveform. label "Speaking".
 *   - idle/ready: NO decorative animation (calm base orb only). label "Ready".
 *   - error     : no decorative state visual (honest, quiet).
 *
 * Motion budget: only transform/opacity animate (GPU); colours are static glows
 * so prefers-reduced-motion can drop motion and keep a faithful STATIC glow.
 * -------------------------------------------------------------------------- */
import type { VoiceStatus } from '../types.ts';

export type { VoiceStatus };

export type ControlAdornment = 'waveform' | 'dots' | 'none';

export interface StateAccent {
  key: VoiceStatus;
  /** control-pill + header subtitle state word. */
  label: string;
  /** control-pill text colour. */
  text: string;
  /** control-pill border colour ('' = keep the idle lavender pill). */
  border: string;
  /** control-pill outer glow (box-shadow colour). */
  glow: string;
  /** control-pill translucent background over the dark card. */
  bg: string;
  /** which adornment flanks the control label. */
  adornment: ControlAdornment;
  /** waveform/dots colour. */
  accent: string;
  /** does the dashboard hero show an animated audio-wave in this state? */
  heroWave: boolean;
  /** hero-wave stroke colours (empty when heroWave is false). */
  heroColors: string[];
}

/** The idle pill text (dark) — matches the existing lavender "Hold to talk". */
const IDLE_TEXT = '#1a1726';

export const STATE_ACCENTS: Record<VoiceStatus, StateAccent> = {
  idle: {
    key: 'idle', label: 'Ready', text: IDLE_TEXT, border: '', glow: '', bg: '',
    adornment: 'none', accent: '', heroWave: false, heroColors: [],
  },
  listening: {
    key: 'listening', label: 'Listening',
    text: '#5eead4', border: '#2dd4bf', glow: 'rgba(45,212,191,0.45)', bg: 'rgba(13,148,136,0.14)',
    adornment: 'waveform', accent: '#2dd4bf',
    heroWave: true, heroColors: ['#2dd4bf', '#22d3ee', '#5eead4'],
  },
  thinking: {
    key: 'thinking', label: 'Thinking',
    text: '#c4b5fd', border: '#8b5cf6', glow: 'rgba(139,92,246,0.45)', bg: 'rgba(109,40,217,0.14)',
    adornment: 'dots', accent: '#a78bfa',
    heroWave: false, heroColors: [],
  },
  speaking: {
    key: 'speaking', label: 'Speaking',
    text: '#fbbf24', border: '#f59e0b', glow: 'rgba(244,63,94,0.45)', bg: 'rgba(234,88,12,0.14)',
    adornment: 'waveform', accent: '#f59e0b',
    heroWave: true, heroColors: ['#f59e0b', '#fb7185', '#e11d90'],
  },
  error: {
    key: 'error', label: 'Something went wrong', text: IDLE_TEXT, border: '', glow: '', bg: '',
    adornment: 'none', accent: '', heroWave: false, heroColors: [],
  },
};

/** True only for the two states with a flowing hero audio-wave. */
export function heroWaveActive(status: VoiceStatus): boolean {
  return STATE_ACCENTS[status].heroWave;
}

/** Which adornment (if any) flanks the control label for a state. */
export function controlAdornment(status: VoiceStatus): ControlAdornment {
  return STATE_ACCENTS[status].adornment;
}

/** The orb state-modifier class (empty for idle/error — base breath only). */
export function orbStateModifier(status: VoiceStatus): string {
  switch (status) {
    case 'listening': return 'bc-aura--listening';
    case 'thinking': return 'bc-aura--thinking';
    case 'speaking': return 'bc-aura--speaking';
    default: return '';
  }
}

/* --- CSS: orb per-state colour + glow. Transform-only keyframes (GPU); the
 *   colour lives in a STATIC box-shadow so reduced-motion keeps a faithful glow.
 *   Higher specificity (.bc-aura.bc-aura--x) than the verbatim BookSesh base so
 *   the idle orb stays byte-faithful and only active states recolour. --- */
export const AURA_STATE_ORB_CSS = `
.bc-aura.bc-aura--listening {
  background: radial-gradient(circle at 35% 30%, #99f6e4 0%, #0d9488 72%);
  box-shadow: 0 0 22px rgba(45,212,191,0.36), 0 0 54px rgba(34,211,238,0.20), 0 0 96px rgba(45,212,191,0.10);
  animation: bc-aura-scale-fast 2.1s ease-in-out infinite;
}
.bc-aura.bc-aura--listening::before {
  display: block;
  background: radial-gradient(circle, rgba(45,212,191,0.22) 0%, rgba(34,211,238,0.11) 38%, rgba(45,212,191,0) 76%);
}
.bc-aura.bc-aura--thinking {
  background: radial-gradient(circle at 35% 30%, #c4b5fd 0%, #6d28d9 72%);
  box-shadow: 0 0 22px rgba(139,92,246,0.34), 0 0 54px rgba(167,139,250,0.18), 0 0 96px rgba(139,92,246,0.09);
  animation: bc-aura-scale-slow 3.4s ease-in-out infinite;
}
.bc-aura.bc-aura--thinking::before {
  display: block;
  background: radial-gradient(circle, rgba(139,92,246,0.22) 0%, rgba(167,139,250,0.11) 38%, rgba(139,92,246,0) 76%);
}
.bc-aura.bc-aura--speaking {
  background: radial-gradient(circle at 38% 32%, #fcd34d 0%, #f59e0b 46%, #ea580c 100%);
  box-shadow: 0 0 24px rgba(244,63,94,0.40), 0 0 60px rgba(225,29,144,0.24), 0 0 110px rgba(251,113,133,0.12);
  animation: bc-aura-scale-speak 1.05s ease-in-out infinite;
}
.bc-aura.bc-aura--speaking::before {
  display: block;
  background: radial-gradient(circle, rgba(244,63,94,0.24) 0%, rgba(225,29,144,0.12) 40%, rgba(244,63,94,0) 78%);
}
@keyframes bc-aura-scale-fast  { 0%,100% { transform: scale(1); } 50% { transform: scale(1.07); } }
@keyframes bc-aura-scale-slow  { 0%,100% { transform: scale(1); } 50% { transform: scale(1.035); } }
@keyframes bc-aura-scale-speak { 0%,100% { transform: scale(1); } 50% { transform: scale(1.10); } }
@media (prefers-reduced-motion: reduce) {
  .bc-aura.bc-aura--listening,
  .bc-aura.bc-aura--thinking,
  .bc-aura.bc-aura--speaking { animation: none !important; }
}
`;

export const HERO_WAVE_STYLE_ID = 'bc-hero-wave-css';

/* --- CSS: hero audio-wave. A wide (200%) layered SVG whose groups drift left by
 *   -50% forever (transform-only, GPU). Sits BEHIND text, pointer-events:none,
 *   never affects layout. Reduced-motion => a STATIC offset, no animation. --- */
export const HERO_WAVE_CSS = `
.bc-hero-wave {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}
.bc-hero-wave svg {
  position: absolute;
  top: 50%;
  left: 0;
  width: 200%;
  height: 150%;
  transform: translateY(-50%);
}
.bc-hero-wave .bc-hw-layer {
  will-change: transform;
  transform: translate3d(0,0,0);
  animation-name: bc-hw-drift;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
@keyframes bc-hw-drift {
  from { transform: translate3d(0,0,0); }
  to   { transform: translate3d(-50%,0,0); }
}
@media (prefers-reduced-motion: reduce) {
  .bc-hero-wave .bc-hw-layer { animation: none !important; transform: translate3d(-14%,0,0); }
}
`;

export const CONTROL_DOTS_STYLE_ID = 'bc-control-dots-css';

/* --- CSS: control processing dots (thinking). Opacity+scale only (GPU).
 *   Reduced-motion => static mid-opacity dots. --- */
export const CONTROL_DOTS_CSS = `
.bc-dots { display: inline-flex; align-items: center; gap: 3px; line-height: 0; }
.bc-dots > i {
  display: block; width: 4px; height: 4px; border-radius: 999px;
  background: currentColor; opacity: 0.3; will-change: transform, opacity;
  animation: bc-dots-pulse 1.2s ease-in-out infinite;
}
@keyframes bc-dots-pulse {
  0%, 100% { opacity: 0.28; transform: scale(0.78); }
  50%      { opacity: 1;    transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .bc-dots > i { animation: none !important; opacity: 0.6; transform: none; }
}
`;
